import * as z from 'zod'

import { SupportedMarketKey } from '@nxt-insurance/constants'

import { getProductFlowConfiguration } from '@flows/configs/shared/flowConfiguration'
import type { FlowConfigProps, StepConfigParams } from '@flows/configs/types'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'
import * as flowConfigUtils from '@flows/insurance/utils'

import { schema } from './schema'
import getSteps from './steps'

export const DENTAL_PRODUCT_KEY = 'dental_v1_getsafe_de' as const

export type DentalFlowConfigProps = FlowConfigProps & {
  /**
   * Platform determines the first step:
   * - 'web': Combined pre_quote step with all three questions
   * - 'native': Separate steps (birthday -> health_insurance_type -> missing_teeth)
   */
  platform?: 'web' | 'native'
}

const getDentalConfig = ({
  clientHelpers,
  subFlow = SubFlow.Dental,
  productKey = DENTAL_PRODUCT_KEY,
  platform = 'web',
}: DentalFlowConfigProps) => {
  const parsedProductKey = z.enum([DENTAL_PRODUCT_KEY]).safeParse(productKey)

  if (!parsedProductKey.success) {
    throw new Error(
      `Assertion: Invalid product key passed to dental flow, expected "${DENTAL_PRODUCT_KEY}" but got ${productKey}`
    )
  }

  const dentalProductKey = parsedProductKey.data

  const stepParams = {
    productKey: dentalProductKey,
    clientHelpers,
    helperFunctions: flowConfigUtils.generateGetFlowValueFunctions(dentalProductKey),
    subFlow,
    market: SupportedMarketKey.De,
  } satisfies StepConfigParams

  const steps = getSteps(stepParams, platform)

  // Web uses combined pre_quote step, native uses separate steps
  const firstStepName = platform === 'native' ? 'dental_birthday' : 'dental_instant'

  return getProductFlowConfiguration(dentalProductKey, steps, {
    constants: {
      FIRST_STEP_NAME: firstStepName,
      SCHEMA: schema,
    },
    getInitialValues: () => ({
      [dentalProductKey]: {
        productKey: dentalProductKey,
        configuration_parameters: {
          tariff_line: '100',
        },
      },
    }),
  })
}

export default getDentalConfig
